// Selector constants used for referencing globo for specific elements.
var SELECTORS = {
    FORM_BUILDER: 'div.globo-formbuilder',
    FORM: 'form.g-container',
    FORM_CONTROL: '.globo-form-control',
    FORM_CONTROL_ERRORED: '.globo-form-control.has-error',
    FORM_CONTROL_CHECKBOX: 'label.globo-option',
    REF_WIDGET_OVERLAY: '.ref__widget--overlay',
    REF_WIDGET_TRIGGER: '.ref__widget--activate > a'
};

var IDENTIFIERS = {

}

/**
 * Gets keyboard-focusable elements within a specified element
 * @param {HTMLElement} element
 * @returns {Array}
 */
function getKeyboardFocusableElements(element) {
    return Array.from(
        [...element.querySelectorAll('a[href], button, input, textarea, select, details,[tabindex]:not([tabindex="-1"])',),
    ].filter(
      el => !el.hasAttribute('disabled') && !el.getAttribute('aria-hidden'),
    )
    );
  }

/**
 * Represents a globo form control and the child select, input, etc. Contains functionality
 * for configuring accessible markup, getting errored state, etc.
 * 
 * @param {HTMLElement} element The input, select, etc with an id formatted like id="[FORM_ID]-select-11" where FORM_ID maps to the data-id attribute on the parent form.
 * @param {number} formId The id of the parent forms see the parent elements data-id attribute.
 * 
 * @returns Functions, properties, etc related to the form control.
 */
function AsxFormControl(element, formId) {
    // If no element or not of type HTMLElement then simply return.
    if (!element || !(element instanceof HTMLElement)) {
        return;
    }

    // Control is related to the current form.
    if (!element.id?.startsWith(formId)) {
        return;
    }

    // Get the parent container this is used to determine if the control is errored.
    var container = element.closest(SELECTORS.FORM_CONTROL);

    // There is no parent container so it isn't a valid form control.
    if (!container) {
        return;
    }

    var tidy = {
        /**
         * Checks if the form control is marked as required using the nested label and if it is required
         * the internal element will be marked with the required attribute (input, select, etc).
         */
        required: function() {
            // Add the required attribute to the select, input, etc if the form control is required.
            if (isRequired() && !element.hasAttribute('required')) {
                element.setAttribute('required', '');
            }
        },
        /**
         * Attempts to add a tabindex to the internal element if it is required. For example a checkbox label
         * needs to be keyboard accessible so that would have the tabindex attribute added.
         */
        tabindex: function() {
            if (element.type !== 'checkbox') {
                return;
            }

            if (!element.nextElementSibling?.hasAttribute('tabindex')) {
                element.nextElementSibling.setAttribute('tabindex', '0');
            }
        },
        /**
         * Determines if the form control contains nested elements that should be marked as group and given a proper
         * accessible label. For example checkes are configured using a ul > li > [checkbox] structure.
         */
        group: function() {
            if (element.type !== 'checkbox') {
                return;
            }

            var ul = container.querySelector('ul');

            if (!ul) {
                return;
            }

            ul.ariaLabel = `Checkbox group for ${getLabel() ?? 'form control'}`;
        }
    };

    /**
     * 
     * @returns 
     */
    function isRequired() {
        return container.querySelector(`label[for="${element.id}"] span.text-danger.text-smaller`)?.innerText?.trim() == '*' ? true : false;
    }

    /**
     * 
     * @returns 
     */
    function isErrored() {
        return container.classList.contains('has-error');
    }

    function getLabel() {
        return container.querySelector('[data-label]')?.innerText.trim();
    }

    function getError() {
        if (!isErrored()) {
            return '';
        }

        container.querySelector('small.messages > p.error').innerText.trim();
    }

    function setError(message) {
        if (!isErrored()) {
            return;
        }

        var error = container.querySelector('small.messages > p.error');

        if (!error) {
            return;
        }

        error.id = `${element.id}-error-msg`;
        error.innerText = message;

        // Link the form control element to the error message.
        element.setAttribute('aria-invalid', 'true');
        element.setAttribute('aria-errormessage', `${element.id}-error-msg`);
    }

    function set() {
        element.setAttribute('aria-invalid', 'true');
        element.setAttribute('aria-errormessage', )
    }

    function focus() {
        element?.focus();
    }

    // Initialize and configure the form control as needed.
    tidy.required();
    tidy.tabindex();
    tidy.group();

    // Public API surface for the AsxFormControl.
    return {
        isRequired,
        isErrored,
        focus,
        getLabel,
        getError,
        setError,
        focus,
        element
    }
}

/**
 * Represents a globo form element. Contains functionality for configuring accessible markup, modifying error handling
 * functionality, etc.
 * 
 * @param {*} formId The id of the globo form that is having accessibility fixes applied to.
 * 
 * @returns Functions, properties, etc related to the form.
 */
function AsxForm(formId) {
    if (!formId) {
        return;
    }

    // Get reference to <form> element associated with the given form id.
    var element = document.querySelector(`form[data-id="${formId}"]`);
    var formControls = Array.from(element.querySelectorAll(`[id*="${formId}-"]`)).map((control) => new AsxFormControl(control, formId));

    var pipeline = {
        bind: function() {
            document.addEventListener('globo.formbuilder.form.beforeSubmit', handlers.onSubmit);
        }
    };

    var handlers = {
        onSubmit: function(e) {
            // This code was taken from globo.formbuilder.bundle.critical.b27da07f589ee94b176b.js
            if (e.target.querySelector(".block-container.errors") && document.activeElement === element.querySelector('button.action.submit')) {
                getFirstErroredControl()?.focus();
            }

            // Adjust required error messages.
            formControls.forEach((formControl) => {
                if (formControl.isRequired() && formControl.isErrored()) {
                    formControl.setError(`${formControl.getLabel()} is required please provide a value`);
                }
            })
        }
    };

    /**
     * 
     * @returns 
     */
    function getErroredControls() {
        return formControls.filter((asxFormControl) => asxFormControl.errored());
    }

    function getFirstErroredControl() {
        return formControls.find((asxFormControl) => asxFormControl.isErrored());
    }

    // Initialize and configure the form as needed.
    pipeline.bind();

    return {
        getErroredControls,
        getFirstErroredControl,
        formControls
    }
}

/**
 * Represents the ref__widget trigger element that toggles open the dialog. Contains fixes
 * for accessibility issues related to the trigger itself and the focus state of the dialog.
 * 
 * @param {*} element The trigger element.
 */
function AsxRefTrigger(element) {
    var pipeline = {
        bind: function() {
            element.addEventListener('click', handlers.onClick);
        }
    }

    var handlers = {
        onClick: function() {
            setTimeout(() => {
                var overlay = document.querySelector('.slick-track .ref__widget--content[aria-hidden="false"]');
    
                if (!overlay) {
                    return;
                }
        
                var focusables = getKeyboardFocusableElements(overlay);
        
                if (focusables && focusables.length > 0) {
                    focusables[0].focus();
                }
            }, 1000);
        }
    };

    pipeline.bind();
}

/**
 * Represents the ref__widget overlay element. Contains fixes for accessibility issues related to
 * configuring the dismiss button and more.
 */
function AsxRefOverlay() {
    var pipeline = {
        tidy: function() {
            // Hide the close button in the body and always display the close button that is for mobile since that
            // is included in the focus trap handles by the dialog.
            document.querySelector('#ref__widget--close-clone').classList.add('hidden');
            var btn = document.querySelector('#ref__widget--close').style.setProperty('display', 'block', 'important');
        }
    }

    pipeline.tidy();
}

/**
 * Waits for the ref__widget trigger to be loaded to the body of the page so a click event
 * can be registered to ensure the dialog receives focus once fully open.
 */
function AsxRefWidgetObserver(callback) {
    var watcher = new MutationObserver(onChange);
    var loadedNodes = [];
    var matches = ['ref__widget--activate', 'ref__widget--overlay'];

    /**
     * Executed when the childList and subtree is modified for document.body this allows us to
     * determine when the ref__widget dialog is injected so we can configure events.
     * 
     * @param {*} mutationList List of the changes within document.body
     * @param {*} observer The observer the mutations are associated with.
     */
    function onChange(mutationList, observer) {
        var isReady = false;

        for (var mutation of mutationList) {
            if (mutation.type !== 'childList' || !mutation.addedNodes || !mutation.addedNodes.length) {
                continue;
            }

            for (let node of mutation.addedNodes) {
                if (node.nodeType === 1 && matches.includes(node.id)) {
                    loadedNodes.push(node);
                }
            }

            if (loadedNodes.length === matches.length) {
                isReady = true;
                break;
            }
        }

        if (isReady) {
            watcher.disconnect();
            callback(loadedNodes.find(x => x.id === matches[0]), loadedNodes.find(x => x.id === matches[1]));
        }
    }

    // If the widget has loaded in 10 seconds make sure the observer is disconnected.
    setTimeout(function() {
        watcher.disconnect();
    }, 10000);

    // Initialize mutation observer.
    watcher.observe(document.body, { attributes: false, childList: true, subtree: true });
}

setTimeout(function() {
    // Construct the accessibility objects then initialize.
    Array.from(document.querySelectorAll(SELECTORS.FORM_BUILDER)).forEach((builder) => {
        var intervalId, timeout = 5000, interval = 100, elapsed = 0;

        var verifyInjection = function() {
            if (elapsed >= timeout) {
                clearInterval(intervalId);
            }

            // Form has not yet been injected into the builder container.
            if (!builder.querySelector(SELECTORS.FORM)) {
                elapsed += interval;
                return;
            }

            clearInterval(intervalId);
            AsxForm(builder.dataset.id);
        };

        intervalId = setInterval(verifyInjection, interval);
    });
}, 1000);

AsxRefWidgetObserver(function(trigger, overlay) {
    AsxRefTrigger(trigger);
    AsxRefOverlay(overlay);
});


// menu fixed
const menuItems = document.querySelectorAll('li.dropdown');
Array.prototype.forEach.call(menuItems, function(el, i){
	el.addEventListener("mouseover", function(event){
		this.classList.add("open");
		clearTimeout(timer);
	});
	el.addEventListener("mouseout", function(event){
		timer = setTimeout(function(event){
			document.querySelector(".dropdown.open").classList.remove('open');
		}, 1000);
	});

    // click
    el.querySelector('a').addEventListener("click",  function(event) {
        console.log(this.parentNode);
		if (!this.parentNode.classList.contains("open")) {
			this.parentNode.classList.add("open");
			this.setAttribute('aria-expanded', "true");
		} else {
            this.parentNode.classList.remove("open");
			this.setAttribute('aria-expanded', "false");
		}
		event.preventDefault();
		return false;
	});

    let links = el.querySelectorAll('a');
    Array.prototype.forEach.call(links, function(el, i){
        el.addEventListener("focus", function() {
            if (timer2) {
                clearTimeout(timer2);
                timer2 = null;
            }
        });
        el.addEventListener("blur", function(event) {
            timer2 = setTimeout(function () {
                let opennav = document.querySelector("nav .dropdown.open")
                if (opennav) {
                    opennav.classList.remove('open');
                    opennav.querySelector('a').setAttribute('aria-expanded', "false");
                }
            }, 10);
        });
    });
});